import { useAuth } from '../contexts/AuthContext';
import { ROLE_PERMISSIONS, UPGRADE_MESSAGES, FeatureKey, RolePermissions } from '../config/permissions';

interface UsePermissionsReturn {
    permissions: RolePermissions;
    canAccess: (feature: FeatureKey) => boolean;
    getLimit: (resource: 'servers' | 'domains') => number;
    isLimitReached: (resource: 'servers' | 'domains', current: number) => boolean;
    getLimitWarning: (resource: 'servers' | 'domains', current: number) => 'none' | 'warning' | 'critical';
    getUpgradeMessage: (feature: FeatureKey | 'serverLimit' | 'domainLimit') => string;
    requiredPlan: (feature: FeatureKey) => string;
    isStarter: boolean;
    isPro: boolean;
    isEnterprise: boolean;
    isAdmin: boolean;
    isExpired: boolean;
}

export const usePermissions = (): UsePermissionsReturn => {
    const { user } = useAuth();

    // Check if subscription is expired (downgrade to starter unless admin)
    const isExpired = !!user?.is_expired;
    let role = user?.role?.toLowerCase() || 'starter';

    if (isExpired && role !== 'admin') {
        role = 'starter';
    }

    const permissions = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.starter;

    const canAccess = (feature: FeatureKey): boolean => {
        return permissions[feature] === true;
    };

    const getLimit = (resource: 'servers' | 'domains'): number => {
        return permissions[resource];
    };

    const isLimitReached = (resource: 'servers' | 'domains', current: number): boolean => {
        const limit = getLimit(resource);
        if (limit === Infinity) return false;
        return current >= limit;
    };

    const getLimitWarning = (resource: 'servers' | 'domains', current: number): 'none' | 'warning' | 'critical' => {
        const limit = getLimit(resource);
        if (limit === Infinity) return 'none';
        if (current >= limit) return 'critical';
        if (current >= limit * 0.8) return 'warning';
        return 'none';
    };

    const getUpgradeMessage = (feature: FeatureKey | 'serverLimit' | 'domainLimit'): string => {
        return UPGRADE_MESSAGES[feature] || 'Upgrade required';
    };

    const requiredPlan = (feature: FeatureKey): string => {
        if (feature === 'browser' || feature === 'support') {
            return 'Enterprise';
        }
        return 'Pro or Enterprise';
    };

    return {
        permissions,
        canAccess,
        getLimit,
        isLimitReached,
        getLimitWarning,
        getUpgradeMessage,
        requiredPlan,
        isStarter: role === 'starter',
        isPro: role === 'pro',
        isEnterprise: role === 'enterprise',
        isAdmin: role === 'admin',
        isExpired: !!user?.is_expired
    };
};

export default usePermissions;
